/* eslint max-len: ["error", 300] */
import React, {PropTypes, Component} from 'react';
import {Promise} from 'es6-promise';
import {connect} from 'react-redux';
import DocumentTitle from 'react-document-title';
import {Field, reduxForm, SubmissionError} from 'redux-form';
import InputWithLabel from './InputWithLabel';
import InputHidden from './InputHidden';
import {showHome, showQuestion} from '../modules/Navigation';
import {createQuestion, updateQuestion, getQuestion} from '../modules/Question';
import {getTags, extractTags, clearTags} from '../modules/Tags';
import {ready} from '../modules/Status';
import {getUser} from '../modules/Authenticate';
import {emitPageView, emitEvent} from '../lib/mz';
import IntroPanel from './components/IntroPanel';
import BackButton from './components/BackButton';
import TagInput from './components/TagInput';
import {mergeArrays} from '../lib/helpers';

require('es6-promise').polyfill();

function validateForm(values) {
  return Promise.resolve().then(() => {
    if (!values.title) {
      throw new SubmissionError({
        title: 'You must provide a title',
        _error: 'Please provide a title ...',
      });
    }
    if (!values.tags) {
      throw new SubmissionError({
        tags: 'Tags can help others to find your question. Please choose at least one.',
        _error: 'Please provide a tag ...',
      });
    }
  });
}

// eslint-disable-next-line react/prefer-stateless-function
class QuestionForm extends Component {
  static propTypes = {
    getUser: PropTypes.func.isRequired,
    getTags: PropTypes.func.isRequired,
    routeParams: PropTypes.object,
    initialValues: PropTypes.object,
    questionId: PropTypes.string,
    error: PropTypes.string,
    reset: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    getQuestion: PropTypes.func.isRequired,
    submit: PropTypes.func.isRequired,
    onHomeClick: PropTypes.func.isRequired,
    onQuestionClick: PropTypes.func.isRequired,
    ready: PropTypes.func.isRequired,
    submitting: PropTypes.bool.isRequired,
    suggestions: PropTypes.array,
    loadingSuggestions: PropTypes.bool.isRequired,
    suggestOnlyBaseTags: PropTypes.bool.isRequired,
    widget: PropTypes.bool.isRequired,
    context: PropTypes.string,
    contextType: PropTypes.string,
    initialize: PropTypes.func.isRequired,
    location: PropTypes.object,
    extractContentTags: PropTypes.func.isRequired,
    titleTags: PropTypes.array,
    contentTags: PropTypes.array,
    clearTags: PropTypes.func.isRequired,
  };

  componentWillMount() {
    this.props.getUser();
    this.props.getTags(this.props.contextType, this.props.suggestOnlyBaseTags);
    const questionId = this.props.routeParams.questionId;
    const questionLoaded = this.props.initialValues && this.props.initialValues.id === +questionId;
    if (questionId && !questionLoaded) {
      this.props.getQuestion(questionId, this.props.contextType);
    } else {
      this.props.clearTags();
      this.props.ready();
    }
  }

  componentDidMount() {
    const questionId = this.props.routeParams.questionId;
    emitPageView({type: questionId ? 'question-create' : 'question-edit', id: questionId});
    this.handleInitialize();
  }

  handleInitialize() {
    this.props.initialize(this.props.initialValues);
  }

  render() {
    const {
      handleSubmit,
      submitting,
      onHomeClick,
      onQuestionClick,
      suggestions,
      loadingSuggestions,
      suggestOnlyBaseTags,
      routeParams,
      widget,
      context,
      initialValues,
      extractContentTags,
      titleTags,
      contentTags,
    } = this.props;

    const onHomeClickHandler = (e) => {
      e.preventDefault();
      onHomeClick(widget, context);
    };

    const slugify = (text) =>
        text.toLowerCase()
            .replace(/[^\w-]+/g, '')      // Remove all non-word chars
            .trim();

    const handleTitleBlur = (e) => {
      const title = e.target.value.split(' ').map((t) => slugify(t));
      extractContentTags('title', title, suggestions);
    };

    const handleContentBlur = (e) => {
      const content = e.target.value.split(' ').map((c) => slugify(c));
      extractContentTags('content', content, suggestions);
    };

    const editingQuestion = routeParams.questionId;
    const returnLink = editingQuestion ? (
      <BackButton onClick={onQuestionClick(routeParams.questionId)}>Return to question</BackButton>
    ) : (
      <BackButton onClick={onHomeClickHandler}>All questions</BackButton>
    );

    const tagField = widget || loadingSuggestions ? null : (
      <Field
        name="tags"
        type="text"
        defaultTag={this.props.location.query.tag}
        component={TagInput}
        contentTags={mergeArrays(titleTags, contentTags)}
        existingTags={initialValues.tags}
        suggestOnlyBaseTags={suggestOnlyBaseTags}
        suggestions={suggestions.map((t) => t.tag)}
        label="Provide at least one tag so others can find your question"
      />);

    const content =
      (<form className="margin-bottom-md margin-bottom-lg-screen-sm" onSubmit={handleSubmit} autoComplete="off">
        <div>
          <Field
            name="title"
            type="text"
            component={InputWithLabel}
            label="What is your question?  Be specific and concise."
            onBlur={handleTitleBlur}
          />
          <Field
            name="content"
            type="textarea"
            rows="10"
            component={InputWithLabel}
            label="Provide details about your question (optional)"
            onBlur={handleContentBlur}
          />
          <Field
            name="context"
            component={InputHidden}
          />
          <Field
            name="context_type"
            component={InputHidden}
          />
          <Field
            name="question_type_id"
            component={InputHidden}
          />
          {tagField}
        </div>
        <div className="action-buttons">
          <div className="action-buttons__item">
            <button type="submit" className="tes-btn tes-btn--primary tes-btn--sm" disabled={submitting}>
              {editingQuestion ? 'Update your question' : 'Post your question'}
            </button>
          </div>
        </div>
      </form>);

    const welcome = (
      <IntroPanel>
        {returnLink}
      </IntroPanel>
    );

    return widget
      ? (
        <div>
          {welcome}
          {content}
        </div>)
      : (
        <DocumentTitle title="Teacher Q&A - Create a Question ...">
          <div>
            {welcome}
            <div className="l-app-padding">
              <h1 className="heading heading--bold margin-bottom-md">
                Get an answer from the Tes Community ...
              </h1>
              {content}
              <hr className="content-breaker" />
              <div className="margin-bottom-ml">
                <h3 className="heading heading--semi-bold heading--underline">Creating a Question</h3>
                <div className="text--xs">
                  <p>We prefer questions that can be answered, not just discussed. Questions that aren’t questions (eg the start of a discussion on a topical event) will be redirected to our Community forums.</p>
                  <p>Try to keep to a single question and topic.</p>
                </div>
              </div>
              <div className="margin-bottom-ml">
                <h3 className="heading heading--semi-bold heading--underline">Formatting Questions</h3>
                <div className="text--xs">
                  <p>You can make things <i>_italic_</i> or <b>*bold*</b></p>
                  <p>For lists, just start each row with a -</p>
                </div>
              </div>
            </div>
          </div>
        </DocumentTitle>);
  }
}

const editableFields = (question, context, contextType) => (question ? {
  id: question.id,
  title: question.title,
  content: question.content,
  context: question.context,
  context_type: question.context_type,
  url: question.url,
  tags: question.tags && question.tags.join(','),
} : {context, context_type: contextType});

const QuestionReduxForm = reduxForm({
  form: 'newQuestion',
})(QuestionForm);

const ConnectedQuestionForm = connect((state) => ({
  initialValues: editableFields(
    state.question.currentQuestion,
    state.environment.widgetConfig.context,
    state.environment.widgetConfig.contextType,
  ),
  suggestions: state.tags.tags,
  loadingSuggestions: state.tags.loadingTags,
  titleTags: state.tags.titleTags,
  contentTags: state.tags.contentTags,
  suggestOnlyBaseTags: state.environment.suggestOnlyBaseTags,
  widget: state.environment.widget,
  context: state.environment.widgetConfig.context,
  contextType: state.environment.widgetConfig.contextType,
}), (dispatch) => ({
  setup: () => {
    dispatch(setup());
  },
  emitPageView: () => {
    dispatch(emitPageView());
  },
  onSubmit: (values) =>
    validateForm(values).then(() => {
      emitEvent('SUBMIT');
      return (values.id ? dispatch(updateQuestion(values)) : dispatch(createQuestion(values)));
    }),
  onHomeClick: (widget, context) => {
    dispatch(showHome(widget, context));
  },
  onQuestionClick: (id) => (e) => {
    e.preventDefault();
    dispatch(showQuestion(id));
  },
  clearTags: () => {
    dispatch(clearTags());
  },
  getQuestion: (url, contextType) => {
    dispatch(getQuestion(url, contextType));
  },
  getUser: () => {
    dispatch(getUser());
  },
  getTags: (contextType, onlyBaseTags) => {
    dispatch(getTags(contextType, onlyBaseTags));
  },
  extractContentTags: (contentType, content, suggestions) => {
    dispatch(extractTags(contentType, content, suggestions));
  },
  ready: () => {
    dispatch(ready());
  },
}))(QuestionReduxForm);

export default ConnectedQuestionForm;
